/**
 * G2eGente - Unified Talent Search with Evaluation System
 * Combines search, filters, and candidate evaluation in one professional interface
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { candidatesAPI, solicitationsAPI, companiesAPI, evaluationsAPI, jobsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Search, 
  MapPin, 
  Briefcase, 
  UserPlus,
  Plus,
  Loader2,
  Users,
  Award,
  Filter,
  X,
  Eye,
  Lock,
  Unlock,
  User,
  Brain,
  Heart,
  GraduationCap,
  Star,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  FileText,
  ClipboardList,
  Save,
  Zap,
  Target,
  Lightbulb,
  TrendingUp,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';

const DISC_TYPES = [
  { value: 'D', label: 'Dominância', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'I', label: 'Influência', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'S', label: 'Estabilidade', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'C', label: 'Conformidade', color: 'bg-blue-100 text-blue-700 border-blue-200' }
];

const EXPERIENCE_LEVELS = [
  { value: '', label: 'Todos os níveis' },
  { value: 'Junior', label: 'Júnior (0-2 anos)' },
  { value: 'Pleno', label: 'Pleno (2-5 anos)' },
  { value: 'Senior', label: 'Sênior (5-10 anos)' },
  { value: 'Especialista', label: 'Especialista (10+)' }
];

// Star rating component
const StarRating = ({ value, onChange, disabled }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star)}
          className={`transition-colors ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
        >
          <Star 
            className={`w-5 h-5 ${
              star <= value 
                ? 'fill-amber-400 text-amber-400' 
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function TalentSearch() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [evaluationTemplate, setEvaluationTemplate] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  const [savingEvaluation, setSavingEvaluation] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [showJobSelectModal, setShowJobSelectModal] = useState(false);
  const [selectedCandidateForInvite, setSelectedCandidateForInvite] = useState(null);
  const [selectedJobForInvite, setSelectedJobForInvite] = useState('');
  const [candidateSolicitations, setCandidateSolicitations] = useState({}); // Track solicitation status per candidate
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedJobForCompare, setSelectedJobForCompare] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    cargos: [], // Multiple cargos support
    experiencia: '',
    localizacao: '',
    competencias: '',
    disc: '',
    pcd: '',
    estagiario: '',
    aprendiz: ''
  });
  const [newCargoInput, setNewCargoInput] = useState('');

  // Evaluation state
  const [evaluation, setEvaluation] = useState({
    niveis: [],
    resumo_geral: '',
    recomendacao: '',
    pontos_fortes: [],
    pontos_melhoria: [],
    job_id: ''
  });
  const [newPontoForte, setNewPontoForte] = useState('');
  const [newPontoMelhoria, setNewPontoMelhoria] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [companyRes, templateRes, jobsRes, solicitationsRes] = await Promise.all([
        companiesAPI.getMe(),
        evaluationsAPI.getTemplate(),
        jobsAPI.getMy(),
        solicitationsAPI.getSent()
      ]);
      setCompany(companyRes.data);
      setEvaluationTemplate(templateRes.data);
      setJobs(jobsRes.data);
      
      // Build map of candidate solicitation status
      const solMap = {};
      solicitationsRes.data.forEach(s => {
        solMap[s.candidate_id] = s.status;
      });
      setCandidateSolicitations(solMap);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      // Multiple cargos - join with comma for backend
      if (filters.cargos.length > 0) params.cargos = filters.cargos.join(',');
      if (filters.experiencia) params.experiencia = filters.experiencia;
      if (filters.localizacao) params.localizacao = filters.localizacao;
      if (filters.competencias) params.competencias = filters.competencias;
      if (filters.disc) params.disc_profile = filters.disc;
      if (filters.pcd) params.pcd = filters.pcd;
      if (filters.estagiario) params.estagiario = filters.estagiario;
      if (filters.aprendiz) params.aprendiz = filters.aprendiz;
      
      const response = await candidatesAPI.search(params);
      setCandidates(response.data);
      
      if (response.data.length === 0) {
        toast.info('Nenhum candidato encontrado com esses filtros');
      } else {
        toast.success(`${response.data.length} candidato(s) encontrado(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao buscar candidatos');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      cargos: [],
      experiencia: '',
      localizacao: '',
      competencias: '',
      disc: '',
      pcd: '',
      estagiario: '',
      aprendiz: ''
    });
    setNewCargoInput('');
  };
  
  const handleAddCargo = () => {
    const cargo = newCargoInput.trim();
    if (cargo && !filters.cargos.includes(cargo)) {
      setFilters(prev => ({
        ...prev,
        cargos: [...prev.cargos, cargo]
      }));
      setNewCargoInput('');
    }
  };
  
  const handleRemoveCargo = (cargoToRemove) => {
    setFilters(prev => ({
      ...prev,
      cargos: prev.cargos.filter(c => c !== cargoToRemove)
    }));
  };

  const activeFiltersCount = filters.cargos.length + Object.entries(filters).filter(([k, v]) => k !== 'cargos' && v).length;

  // Open modal to select job before inviting
  const handleOpenInviteModal = (candidate) => {
    setSelectedCandidateForInvite(candidate);
    setSelectedJobForInvite('');
    setShowJobSelectModal(true);
  };
  
  // Send invitation with selected job
  const handleSendInvitation = async () => {
    if (!selectedCandidateForInvite || !selectedJobForInvite) {
      toast.error('Selecione uma vaga para convidar o candidato');
      return;
    }
    
    setRequesting(selectedCandidateForInvite.id);
    try {
      await solicitationsAPI.create({ 
        candidate_id: selectedCandidateForInvite.id,
        job_id: selectedJobForInvite 
      });
      toast.success('Convite enviado ao candidato!');
      setCandidateSolicitations(prev => ({
        ...prev,
        [selectedCandidateForInvite.id]: 'PENDING'
      }));
      setShowJobSelectModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar convite');
    } finally {
      setRequesting(null);
    }
  };

  const handleRequestAccess = async (candidateId) => {
    // This function is kept for backward compatibility but now redirects to modal
    const candidate = candidates.find(c => c.id === candidateId);
    if (candidate) {
      handleOpenInviteModal(candidate);
    }
  };

  const handleViewCandidate = async (candidate) => {
    // Check if candidate has approved solicitation
    const solicitationStatus = candidateSolicitations[candidate.id];
    
    if (solicitationStatus !== 'APPROVED') {
      toast.error('Dados disponíveis apenas após aprovação do candidato');
      return;
    }
    
    setSelectedCandidate(candidate);
    setLoadingDetail(true);
    setShowEvaluation(false);
    
    try {
      const response = await candidatesAPI.getById(candidate.id);
      setCandidateDetail(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setCandidateDetail(null);
        toast.error('Acesso não autorizado');
      } else {
        toast.error('Erro ao carregar candidato');
      }
    } finally {
      setLoadingDetail(false);
    }
  };

  const initializeEvaluation = async (jobId = null) => {
    if (!evaluationTemplate || !selectedCandidate) return;
    
    // Set loading state first to prevent Sheet from opening
    setLoadingEvaluation(true);
    
    // Try to load existing evaluation for this candidate
    try {
      const response = await evaluationsAPI.getAll({ 
        candidate_id: selectedCandidate.id,
        job_id: jobId || undefined
      });
      
      if (response.data && response.data.length > 0) {
        // Load existing evaluation
        const existingEval = response.data[0];
        setEvaluation({
          id: existingEval.id,
          niveis: existingEval.niveis || [],
          resumo_geral: existingEval.resumo_geral || '',
          recomendacao: existingEval.recomendacao || '',
          pontos_fortes: existingEval.pontos_fortes || [],
          pontos_melhoria: existingEval.pontos_melhoria || [],
          job_id: existingEval.job_id || '',
          status: existingEval.status
        });
        setShowEvaluation(true);
        setLoadingEvaluation(false);
        return;
      }
    } catch (error) {
      console.log('No existing evaluation found, creating new one');
    }
    
    // Create new evaluation from template
    const niveis = Object.entries(evaluationTemplate).map(([key, nivel]) => ({
      nivel: key,
      nome: nivel.nome,
      descricao: nivel.descricao,
      steps: nivel.steps.map(step => ({
        nome: step.nome,
        descricao: step.descricao,
        pontuacao: 0,
        observacao: ''
      })),
      comentario: ''
    }));
    
    setEvaluation({
      niveis,
      resumo_geral: '',
      recomendacao: '',
      pontos_fortes: [],
      pontos_melhoria: [],
      job_id: jobId || ''
    });
    setShowEvaluation(true);
    setLoadingEvaluation(false);
  };

  const updateStepScore = (nivelIndex, stepIndex, pontuacao) => {
    setEvaluation(prev => {
      const newNiveis = [...prev.niveis];
      newNiveis[nivelIndex].steps[stepIndex].pontuacao = pontuacao;
      return { ...prev, niveis: newNiveis };
    });
  };

  const updateStepObservacao = (nivelIndex, stepIndex, observacao) => {
    setEvaluation(prev => {
      const newNiveis = [...prev.niveis];
      newNiveis[nivelIndex].steps[stepIndex].observacao = observacao;
      return { ...prev, niveis: newNiveis };
    });
  };

  const updateNivelComentario = (nivelIndex, comentario) => {
    setEvaluation(prev => {
      const newNiveis = [...prev.niveis];
      newNiveis[nivelIndex].comentario = comentario;
      return { ...prev, niveis: newNiveis };
    });
  };

  const addPontoForte = () => {
    if (newPontoForte.trim()) {
      setEvaluation(prev => ({
        ...prev,
        pontos_fortes: [...prev.pontos_fortes, newPontoForte.trim()]
      }));
      setNewPontoForte('');
    }
  };

  const addPontoMelhoria = () => {
    if (newPontoMelhoria.trim()) {
      setEvaluation(prev => ({
        ...prev,
        pontos_melhoria: [...prev.pontos_melhoria, newPontoMelhoria.trim()]
      }));
      setNewPontoMelhoria('');
    }
  };

  const removePonto = (type, index) => {
    setEvaluation(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const calculateNivelMedia = (nivel) => {
    const scores = nivel.steps.filter(s => s.pontuacao > 0).map(s => s.pontuacao);
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : '-';
  };

  const calculateOverallMedia = () => {
    const allScores = evaluation.niveis.flatMap(n => n.steps.filter(s => s.pontuacao > 0).map(s => s.pontuacao));
    return allScores.length > 0 ? (allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(1) : '-';
  };

  const handleSaveEvaluation = async (status = 'rascunho') => {
    if (!selectedCandidate) return;
    
    setSavingEvaluation(true);
    try {
      const evaluationData = {
        candidate_id: selectedCandidate.id,
        job_id: evaluation.job_id || null,
        niveis: evaluation.niveis,
        resumo_geral: evaluation.resumo_geral,
        recomendacao: evaluation.recomendacao,
        pontos_fortes: evaluation.pontos_fortes,
        pontos_melhoria: evaluation.pontos_melhoria,
        status
      };
      
      if (evaluation.id) {
        // Update existing evaluation
        await evaluationsAPI.update(evaluation.id, evaluationData);
      } else {
        // Create new evaluation
        await evaluationsAPI.create(evaluationData);
      }
      
      toast.success(status === 'finalizado' ? 'Avaliação finalizada!' : 'Rascunho salvo!');
      setShowEvaluation(false);
      setSelectedCandidate(null);
      setCandidateDetail(null);
      setEvaluation({
        niveis: [],
        resumo_geral: '',
        recomendacao: '',
        pontos_fortes: [],
        pontos_melhoria: [],
        job_id: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar avaliação');
    } finally {
      setSavingEvaluation(false);
    }
  };

  const hasFullAccess = company?.acesso_total_lancamento || company?.plano_info?.busca_global;

  const getNivelIcon = (nivel) => {
    const icons = {
      entrega: Target,
      comportamento: Heart,
      cognitivo: Brain,
      aspiracao: Lightbulb,
      experiencia: Briefcase,
      lideranca: Crown
    };
    return icons[nivel] || Star;
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="talent-search">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Busca de Talentos</h1>
            <p className="text-slate-600">Encontre e avalie candidatos de forma integrada</p>
          </div>
          
          {hasFullAccess ? (
            <Badge className="bg-emerald-100 text-emerald-700 px-4 py-2">
              <Unlock className="w-4 h-4 mr-2" />
              Acesso Total Ativo
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 px-4 py-2">
              <Lock className="w-4 h-4 mr-2" />
              Acesso via Consentimento
            </Badge>
          )}
        </div>

        {/* Search Filters Card */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            {/* Multiple Cargos Input */}
            <div className="space-y-3 mb-6">
              <Label className="text-sm font-medium text-slate-700">Cargos (múltiplos)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Digite um cargo e pressione Enter"
                    value={newCargoInput}
                    onChange={(e) => setNewCargoInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCargo())}
                    className="pl-10"
                    data-testid="filter-cargo-input"
                  />
                </div>
                <Button type="button" variant="outline" onClick={handleAddCargo}>
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
              {filters.cargos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filters.cargos.map((cargo, i) => (
                    <Badge 
                      key={i} 
                      variant="secondary"
                      className="bg-cyan-50 text-cyan-700 cursor-pointer hover:bg-red-50 hover:text-red-700 transition-colors"
                      onClick={() => handleRemoveCargo(cargo)}
                    >
                      {cargo}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Adicione múltiplos cargos para ampliar a busca (Ex: Desenvolvedor, Programador, Analista de Sistemas)
              </p>
            </div>
            
            {/* Main Search Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Nível de Experiência</Label>
                <Select
                  value={filters.experiencia}
                  onValueChange={(v) => setFilters({...filters, experiencia: v})}
                >
                  <SelectTrigger data-testid="filter-experiencia">
                    <SelectValue placeholder="Todos os níveis" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value || 'all'}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Localização</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cidade ou Estado"
                    value={filters.localizacao}
                    onChange={(e) => setFilters({...filters, localizacao: e.target.value})}
                    className="pl-10"
                    data-testid="filter-localizacao"
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Competências</Label>
                <Input
                  placeholder="Ex: Python, Excel, Gestão"
                  value={filters.competencias}
                  onChange={(e) => setFilters({...filters, competencias: e.target.value})}
                  data-testid="filter-competencias"
                />
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {/* DISC Profile */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <Brain className="w-3 h-3" /> Perfil DISC
                </Label>
                <Select
                  value={filters.disc}
                  onValueChange={(v) => setFilters({...filters, disc: v === 'all' ? '' : v})}
                >
                  <SelectTrigger data-testid="filter-disc">
                    <SelectValue placeholder="Qualquer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Qualquer</SelectItem>
                    {DISC_TYPES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.value} - {d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* PcD Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <Heart className="w-3 h-3" /> PcD
                </Label>
                <Select
                  value={filters.pcd}
                  onValueChange={(v) => setFilters({...filters, pcd: v === 'all' ? '' : v})}
                >
                  <SelectTrigger data-testid="filter-pcd">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os perfis</SelectItem>
                    <SelectItem value="tambem">Incluir PcD</SelectItem>
                    <SelectItem value="somente">Somente PcD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Estagiário Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" /> Estagiário
                </Label>
                <Select
                  value={filters.estagiario}
                  onValueChange={(v) => setFilters({...filters, estagiario: v === 'all' ? '' : v})}
                >
                  <SelectTrigger data-testid="filter-estagiario">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os perfis</SelectItem>
                    <SelectItem value="tambem">Incluir Estagiários</SelectItem>
                    <SelectItem value="somente">Somente Estagiários</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Aprendiz Filter */}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <Award className="w-3 h-3" /> Aprendiz
                </Label>
                <Select
                  value={filters.aprendiz}
                  onValueChange={(v) => setFilters({...filters, aprendiz: v === 'all' ? '' : v})}
                >
                  <SelectTrigger data-testid="filter-aprendiz">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os perfis</SelectItem>
                    <SelectItem value="tambem">Incluir Aprendizes</SelectItem>
                    <SelectItem value="somente">Somente Aprendizes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Search Button */}
              <div className="flex items-end">
                <Button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  data-testid="search-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Buscar
                </Button>
              </div>
            </div>

            {/* Active Filters */}
            {activeFiltersCount > 0 && (
              <div className="flex items-center gap-2 pt-4 border-t">
                <span className="text-sm text-slate-500">Filtros ativos:</span>
                <Badge variant="secondary">{activeFiltersCount}</Badge>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 ml-auto">
                  <X className="w-4 h-4 mr-1" />
                  Limpar filtros
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CNPJ Lock Info */}
        <div className="flex items-center gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
          <Lock className="w-5 h-5 text-cyan-600" />
          <p className="text-sm text-cyan-700">
            <strong>Trava por CNPJ:</strong> Candidatos da sua empresa são automaticamente ocultados para garantir ética no recrutamento.
          </p>
        </div>

        {/* Results - 50/50 Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Candidates List */}
          <div className="space-y-4">
            {candidates.length === 0 && !loading ? (
              <Card className="border-slate-200">
                <CardContent className="py-16 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    {activeFiltersCount > 0 ? 'Nenhum candidato encontrado' : 'Faça uma busca'}
                  </h3>
                  <p className="text-slate-600">
                    Use os filtros acima para encontrar candidatos
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">{candidates.length} candidato(s) encontrado(s)</p>
                </div>
                
                {candidates.map((candidate) => {
                  const solicitationStatus = candidateSolicitations[candidate.id];
                  const isApproved = solicitationStatus === 'APPROVED';
                  const isPending = solicitationStatus === 'PENDING';
                  const isRejected = solicitationStatus === 'REJECTED';
                  
                  return (
                    <Card 
                      key={candidate.id}
                      className={`border-slate-200 transition-all ${
                        selectedCandidate?.id === candidate.id ? 'ring-2 ring-cyan-500' : ''
                      } ${isApproved ? 'hover:shadow-md cursor-pointer' : ''}`}
                      data-testid={`candidate-card-${candidate.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            {isApproved ? (
                              <User className="w-6 h-6 text-cyan-600" />
                            ) : (
                              <Lock className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Tags Row */}
                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                              {/* Solicitation Status Badge */}
                              {isApproved && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Aprovado
                                </Badge>
                              )}
                              {isPending && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Pendente
                                </Badge>
                              )}
                              {isRejected && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <X className="w-3 h-3 mr-1" />
                                  Rejeitado
                                </Badge>
                              )}
                              {candidate.is_pcd && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">PcD</Badge>
                              )}
                              {candidate.is_50_plus && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">50+</Badge>
                              )}
                              {candidate.is_estagiario && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Estagiário</Badge>
                              )}
                              {candidate.is_aprendiz && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Aprendiz</Badge>
                              )}
                              {candidate.disc_profile && (
                                <Badge variant="outline" className="text-xs">
                                  <Brain className="w-3 h-3 mr-1" />
                                  {candidate.disc_profile}
                                </Badge>
                              )}
                            </div>
                            
                            <h3 className="font-semibold text-slate-900 truncate">{candidate.cargo}</h3>
                            
                            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-slate-500">
                              {candidate.experiencia && (
                                <span className="flex items-center gap-1">
                                  <Briefcase className="w-3.5 h-3.5" />
                                  {candidate.experiencia}
                                </span>
                              )}
                              {candidate.localizacao && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {candidate.localizacao}
                                </span>
                              )}
                            </div>
                            
                            {candidate.competencias?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {candidate.competencias.slice(0, 4).map((comp, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs bg-slate-100">
                                    {comp}
                                  </Badge>
                                ))}
                                {candidate.competencias.length > 4 && (
                                  <Badge variant="secondary" className="text-xs bg-slate-100">
                                    +{candidate.competencias.length - 4}
                                  </Badge>
                                )}
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                              {isApproved ? (
                                <Button 
                                  size="sm" 
                                  className="bg-cyan-600 hover:bg-cyan-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewCandidate(candidate);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  Ver Detalhes
                                </Button>
                              ) : isPending ? (
                                <Button size="sm" variant="outline" disabled>
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Aguardando Aprovação
                                </Button>
                              ) : isRejected ? (
                                <Button size="sm" variant="outline" disabled className="text-red-600">
                                  <X className="w-3 h-3 mr-1" />
                                  Acesso Negado
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  className="bg-cyan-600 hover:bg-cyan-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenInviteModal(candidate);
                                  }}
                                  disabled={requesting === candidate.id}
                                >
                                  {requesting === candidate.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <UserPlus className="w-3 h-3 mr-1" />
                                  )}
                                  Convidar para Seleção
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            )}
          </div>

          {/* Candidate Detail / Evaluation Panel */}
          <div className="space-y-4">
            {selectedCandidate ? (
              <Card className="border-slate-200 sticky top-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Detalhes do Candidato</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedCandidate(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingDetail ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
                    </div>
                  ) : candidateDetail ? (
                    <>
                      {/* Profile Header */}
                      <div className="flex items-center gap-4">
                        {candidateDetail.foto_url ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${candidateDetail.foto_url}`}
                            alt={candidateDetail.nome}
                            className="w-16 h-16 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-slate-100 rounded-xl flex items-center justify-center">
                            <User className="w-8 h-8 text-cyan-600" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-slate-900">{candidateDetail.nome || 'Nome não informado'}</h3>
                          <p className="text-sm text-slate-600">{candidateDetail.cargo}</p>
                          {candidateDetail.email && (
                            <p className="text-xs text-cyan-600">{candidateDetail.email}</p>
                          )}
                        </div>
                      </div>
                      
                      <Separator />
                      
                      {/* Quick Info */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {candidateDetail.localizacao && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <MapPin className="w-4 h-4" />
                            {candidateDetail.localizacao}
                          </div>
                        )}
                        {candidateDetail.telefone && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <FileText className="w-4 h-4" />
                            {candidateDetail.telefone}
                          </div>
                        )}
                        {candidateDetail.disc_profile && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Brain className="w-4 h-4" />
                            DISC: {candidateDetail.disc_profile}
                          </div>
                        )}
                        {candidateDetail.disponibilidade_remoto && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Zap className="w-4 h-4" />
                            {candidateDetail.disponibilidade_remoto}
                          </div>
                        )}
                      </div>
                      
                      {candidateDetail.resumo && (
                        <>
                          <Separator />
                          <div>
                            <Label className="text-xs text-slate-500">Resumo</Label>
                            <p className="text-sm text-slate-700 mt-1">{candidateDetail.resumo}</p>
                          </div>
                        </>
                      )}
                      
                      {candidateDetail.curriculo_url && (
                        <Button variant="outline" className="w-full" asChild>
                          <a 
                            href={`${process.env.REACT_APP_BACKEND_URL}${candidateDetail.curriculo_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Ver Currículo
                          </a>
                        </Button>
                      )}
                      
                      <Separator />
                      
                      {/* Evaluation Button */}
                      <Button 
                        onClick={initializeEvaluation}
                        className="w-full bg-cyan-600 hover:bg-cyan-700"
                      >
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Iniciar Avaliação de Entrevista
                      </Button>
                      
                      {/* Compare with Jobs Button */}
                      <Button 
                        onClick={() => setShowCompareModal(true)}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                      >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Comparar com Vagas
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <Lock className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                      <p className="text-sm text-slate-600 mb-4">
                        Você precisa de acesso aprovado para ver os detalhes deste candidato.
                      </p>
                      <Button 
                        onClick={() => handleRequestAccess(selectedCandidate.id)}
                        disabled={requesting === selectedCandidate.id}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        {requesting === selectedCandidate.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        Solicitar Acesso
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200 border-dashed">
                <CardContent className="py-12 text-center">
                  <Eye className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Selecione um candidato para ver detalhes</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Evaluation Full Screen Dialog */}
        <Dialog open={showEvaluation} onOpenChange={(open) => {
          setShowEvaluation(open);
          if (!open) {
            setSelectedCandidate(null);
            setCandidateDetail(null);
            setEvaluation({
              niveis: [],
              resumo_geral: '',
              recomendacao: '',
              pontos_fortes: [],
              pontos_melhoria: [],
              job_id: ''
            });
          }
        }}>
          <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-cyan-600" />
                </div>
                Avaliação de Entrevista
              </DialogTitle>
            </DialogHeader>
            
            {selectedCandidate && candidateDetail && evaluation.niveis?.length > 0 && (
              <div className="flex-1 overflow-y-auto mt-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Candidate Info & Overall Score */}
                  <div className="space-y-4">
                    {/* Candidate Info */}
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-xl flex items-center justify-center">
                            <User className="w-7 h-7 text-cyan-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{candidateDetail.nome || 'Candidato'}</h3>
                            <p className="text-sm text-slate-600">{candidateDetail.cargo}</p>
                            {candidateDetail.disc_profile && (
                              <Badge variant="outline" className="mt-1">
                                <Brain className="w-3 h-3 mr-1" />
                                DISC: {candidateDetail.disc_profile}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Job Selection */}
                    <Card className="border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-slate-600">Vaga Relacionada</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <Select
                          value={evaluation.job_id || 'none'}
                          onValueChange={(v) => setEvaluation({...evaluation, job_id: v === 'none' ? '' : v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma vaga" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhuma vaga específica</SelectItem>
                            {jobs.map(job => (
                              <SelectItem key={job.id} value={job.id}>{job.titulo}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </CardContent>
                    </Card>

                    {/* Overall Score Display */}
                    <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-cyan-100">
                      <CardContent className="p-6">
                        <div className="text-center">
                          <span className="text-sm font-medium text-cyan-700 uppercase tracking-wide">Média Geral</span>
                          <div className="flex items-center justify-center gap-2 mt-2">
                            <Star className="w-8 h-8 fill-amber-400 text-amber-400" />
                            <span className="text-5xl font-bold text-cyan-900">{calculateOverallMedia()}</span>
                            <span className="text-xl text-cyan-600">/5</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Quick Navigation - Níveis */}
                    <Card className="border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-slate-600">Níveis de Avaliação</CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 pt-0">
                        <div className="grid grid-cols-2 gap-2">
                          {evaluation.niveis.map((nivel) => {
                            const Icon = getNivelIcon(nivel.nivel);
                            const media = calculateNivelMedia(nivel);
                            return (
                              <div 
                                key={nivel.nivel}
                                className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                              >
                                <Icon className="w-4 h-4 text-cyan-600" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-700 truncate capitalize">{nivel.nivel}</p>
                                </div>
                                <Badge variant="outline" className="text-xs">{media}</Badge>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Column - Evaluation Tabs (2 cols wide) */}
                  <div className="lg:col-span-2">
                    <Card className="border-slate-200 h-full">
                      <CardContent className="p-4">
                        <Tabs defaultValue="entrega" className="w-full">
                          <TabsList className="w-full grid grid-cols-6 h-auto gap-1 p-1 bg-slate-100">
                            {evaluation.niveis.map((nivel) => {
                              const Icon = getNivelIcon(nivel.nivel);
                              return (
                                <TabsTrigger 
                                  key={nivel.nivel} 
                                  value={nivel.nivel}
                                  className="flex flex-col py-3 px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                                >
                                  <Icon className="w-5 h-5 mb-1" />
                                  <span className="text-xs text-center leading-tight capitalize">{nivel.nivel}</span>
                                  <span className="text-sm font-bold text-cyan-600">{calculateNivelMedia(nivel)}</span>
                                </TabsTrigger>
                              );
                            })}
                          </TabsList>
                          
                          {evaluation.niveis.map((nivel, nivelIndex) => (
                            <TabsContent key={nivel.nivel} value={nivel.nivel} className="mt-4 space-y-4">
                              <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border-l-4 border-cyan-500">
                                <h4 className="font-semibold text-slate-900 text-lg">{nivel.nome}</h4>
                                <p className="text-sm text-slate-600 mt-1">{nivel.descricao}</p>
                              </div>
                              
                              {/* Steps Grid */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {nivel.steps.map((step, stepIndex) => (
                                  <div key={stepIndex} className="p-4 border rounded-lg space-y-3 bg-white hover:shadow-sm transition-shadow">
                                    <div className="flex items-start justify-between gap-2">
                                      <Label className="font-medium text-slate-800">{step.nome}</Label>
                                      <div className="flex gap-0.5 flex-shrink-0">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            type="button"
                                            onClick={() => updateStepScore(nivelIndex, stepIndex, star)}
                                            className="p-0.5 hover:scale-110 transition-transform"
                                          >
                                            <Star className={`w-5 h-5 ${
                                              star <= (step.pontuacao || 0) 
                                                ? 'fill-amber-400 text-amber-400' 
                                                : 'text-slate-300'
                                            }`} />
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    <Textarea
                                      placeholder="Observações..."
                                      value={step.observacao || ''}
                                      onChange={(e) => updateStepObservacao(nivelIndex, stepIndex, e.target.value)}
                                      className="text-sm min-h-[60px] resize-none"
                                    />
                                  </div>
                                ))}
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Bottom Section - Pontos Fortes/Melhoria + Recomendação */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                  {/* Pontos Fortes */}
                  <Card className="border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Pontos Fortes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Adicionar ponto forte..."
                          value={newPontoForte}
                          onChange={(e) => setNewPontoForte(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addPontoForte()}
                          className="text-sm"
                        />
                        <Button size="sm" variant="outline" onClick={addPontoForte} className="text-green-600">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                        {evaluation.pontos_fortes?.map((ponto, i) => (
                          <Badge 
                            key={i} 
                            className="bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700 cursor-pointer transition-colors"
                            onClick={() => removePonto('pontos_fortes', i)}
                          >
                            {ponto}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pontos de Melhoria */}
                  <Card className="border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Pontos de Melhoria
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Adicionar ponto de melhoria..."
                          value={newPontoMelhoria}
                          onChange={(e) => setNewPontoMelhoria(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addPontoMelhoria()}
                          className="text-sm"
                        />
                        <Button size="sm" variant="outline" onClick={addPontoMelhoria} className="text-amber-600">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                        {evaluation.pontos_melhoria?.map((ponto, i) => (
                          <Badge 
                            key={i} 
                            className="bg-amber-100 text-amber-700 hover:bg-red-100 hover:text-red-700 cursor-pointer transition-colors"
                            onClick={() => removePonto('pontos_melhoria', i)}
                          >
                            {ponto}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recomendação Final */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-700">Recomendação Final</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'contratar', label: 'Contratar', color: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' },
                          { value: 'revisar', label: 'Revisar', color: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200' },
                          { value: 'nao_contratar', label: 'Não Contratar', color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' }
                        ].map(opt => (
                          <Button
                            key={opt.value}
                            variant="outline"
                            className={`h-auto py-3 ${evaluation.recomendacao === opt.value ? opt.color : ''}`}
                            onClick={() => setEvaluation({...evaluation, recomendacao: opt.value})}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t mt-6">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-12"
                    onClick={() => handleSaveEvaluation('rascunho')}
                    disabled={savingEvaluation}
                  >
                    {savingEvaluation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar Rascunho
                  </Button>
                  <Button 
                    className="flex-1 h-12 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => handleSaveEvaluation('finalizado')}
                    disabled={savingEvaluation}
                  >
                    {savingEvaluation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Finalizar Avaliação
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Job Selection Modal for Invitation */}
        <Dialog open={showJobSelectModal} onOpenChange={setShowJobSelectModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-cyan-600" />
                Convidar para Seleção
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Para qual vaga você deseja convidar este candidato?
              </p>
              
              {selectedCandidateForInvite && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{selectedCandidateForInvite.cargo}</p>
                    <p className="text-xs text-slate-500">{selectedCandidateForInvite.localizacao}</p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Selecione a Vaga</Label>
                {jobs.length === 0 ? (
                  <div className="p-4 bg-amber-50 rounded-lg text-amber-700 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Você ainda não tem vagas cadastradas. Crie uma vaga primeiro.
                  </div>
                ) : (
                  <Select
                    value={selectedJobForInvite}
                    onValueChange={setSelectedJobForInvite}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma vaga..." />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.titulo} - {job.localizacao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-cyan-50 rounded-lg text-sm text-cyan-700">
                <Lock className="w-4 h-4 flex-shrink-0" />
                <span>Os dados pessoais do candidato só serão revelados após a aprovação do convite.</span>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowJobSelectModal(false)}>
                Cancelar
              </Button>
              <Button 
                className="bg-cyan-600 hover:bg-cyan-700"
                onClick={handleSendInvitation}
                disabled={!selectedJobForInvite || requesting}
              >
                {requesting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                Enviar Convite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Job Selection Modal for Profile Comparison */}
        <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                Comparar com Vaga
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Selecione uma vaga para comparar o perfil deste candidato com o perfil ideal.
              </p>
              
              {selectedCandidate && candidateDetail && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{candidateDetail.nome || candidateDetail.cargo}</p>
                    <p className="text-xs text-slate-500">DISC: {candidateDetail.disc_profile || 'Não definido'}</p>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Selecione a Vaga</Label>
                {jobs.filter(j => j.perfil_ideal).length === 0 ? (
                  <div className="p-4 bg-amber-50 rounded-lg text-amber-700 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Nenhuma vaga tem perfil ideal configurado. Configure o perfil ideal nas vagas para usar esta funcionalidade.
                  </div>
                ) : (
                  <Select
                    value={selectedJobForCompare}
                    onValueChange={setSelectedJobForCompare}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma vaga..." />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.filter(j => j.perfil_ideal).map(job => (
                        <SelectItem key={job.id} value={job.id}>
                          <div className="flex items-center gap-2">
                            <span>{job.titulo}</span>
                            {job.perfil_ideal?.disc_desejado && (
                              <Badge variant="outline" className="text-xs">
                                DISC: {job.perfil_ideal.disc_desejado}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <TrendingUp className="w-4 h-4 flex-shrink-0" />
                <span>Você será direcionado para o relatório comparativo da vaga selecionada.</span>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompareModal(false)}>
                Cancelar
              </Button>
              <Button 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  if (selectedJobForCompare) {
                    navigate(`/company/jobs/${selectedJobForCompare}/comparison`);
                    setShowCompareModal(false);
                  }
                }}
                disabled={!selectedJobForCompare}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Ver Comparativo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
